// Access granted by hand, recorded so that it survives.
//
// An officer can give an account `paid` or `vip` access from the admin
// permissions screen, with no payment behind it. That is a supported product
// state (a comped account, a tester, a partner), decided on 2026-08-03.
//
// The problem it used to have: `reconcileLicenseState` recomputes an account's
// access from its stored payment records, one "block" per provider. A grant
// made by writing the role alone leaves no block at all, so the reconciler
// finds nothing to justify the access and clears the role. Roles of `Mod` and
// above are the exception - the reconciler preserves those on every path - but
// `Paid` and `Vip` are not preserved, so exactly the two roles this screen
// hands out are the two that get cleared.
//
// The fix is to record the grant as a real block the reconciler already reads:
// `keyData`, the same block a redeemed activation key writes. It already
// carries a status, an expiry, a granted role and a redemption time, and every
// server currently running already honours it, so nothing needs a new
// `_users` document format version.
//
// The one thing that has to stay distinguishable is a hand-made grant from a
// genuine key redemption, because they must be treated differently: a real
// redemption is somebody's purchase and must never be overwritten. The marker
// is the activation code. A real redemption carries the key the user typed in;
// a hand-made grant carries a made-up code starting with one of the prefixes
// below.

import { DateTime } from 'luxon';
import { AuthRole, authRoleFromStrings } from '../couch-auth';
import { CouchUserInfo, CouchUserKeyData, NEVER_EXPIRES, SubscriptionStatus, UserLicenseType } from './couch-user';
import { isBlockLive } from '../subscription-repoint';
import { roleAfterLicenceExpiry } from '../effective-role';

/** The two fields that decide whether a provider block is still live. Every
 *  provider block carries them, and so do the account's top-level fields. */
type SubscriptionAccessWindow = { subscriptionStatus?: SubscriptionStatus; expiresDate_utc?: string };

/**
 * Prefix on the made-up activation code of a grant an officer makes from the
 * admin permissions screen. Anything after it is provenance, not meaning -
 * nothing parses the rest of the code.
 */
export const OFFICER_GRANT_CODE_PREFIX = 'admin-grant';

/**
 * Prefix written by the May 2026 PayPro goodwill grant, which established this
 * whole convention. `tools/src/paypro-compliance-2026-05-19-shared.ts`
 * re-exports this as its own `SYNTHETIC_PREFIX`, so the codes those scripts
 * wrote and the codes the app recognises cannot drift apart.
 */
export const PAYPRO_COMPLIANCE_2026_05_19_CODE_PREFIX = 'paypro-compliance-2026-05-19';

/**
 * Prefix written by the one-off backfill that records access an account
 * ALREADY had, for accounts granted before the admin screen started recording
 * anything. Kept apart from the officer prefix on purpose: an officer did not
 * make these grants today, the account already held the access, and the
 * backfill only wrote down what was already true. Anyone reading an audit log
 * later can tell the two apart.
 */
export const LEGACY_ACCESS_RESTORE_CODE_PREFIX = 'legacy-access-restore-2026-08-04';

/**
 * Every prefix we have ever written on a made-up activation code.
 *
 * Never remove one. The codes are already written on live accounts, and
 * dropping a prefix turns those grants into things that look like real
 * redemptions, which the officer screen then refuses to touch.
 */
export const SYNTHETIC_ACTIVATION_CODE_PREFIXES: readonly string[] = [
  OFFICER_GRANT_CODE_PREFIX,
  PAYPRO_COMPLIANCE_2026_05_19_CODE_PREFIX,
  LEGACY_ACCESS_RESTORE_CODE_PREFIX,
];

/**
 * True when this activation code is one we made up ourselves rather than a key
 * a user redeemed.
 *
 * A block with NO activation code at all counts as synthetic. That is
 * deliberate: a real redemption always records the code that was redeemed, so
 * a block without one cannot be a redemption, and treating it as untouchable
 * would leave an account nobody can ever fix from the officer screen.
 */
export function isSyntheticActivationCode(activationCode: string | undefined): boolean {
  if (!activationCode) return true;
  return SYNTHETIC_ACTIVATION_CODE_PREFIXES.some(prefix => activationCode.startsWith(prefix));
}

/**
 * True when this `keyData` block records a genuine activation key that a user
 * redeemed, which is the one case a hand-made grant must refuse to overwrite.
 *
 * `undefined` (no block at all) is not a redemption, so this answers false.
 */
export function isRealKeyRedemption(keyData: CouchUserKeyData | undefined): boolean {
  if (!keyData) return false;
  return !isSyntheticActivationCode(keyData.activationCode);
}

/**
 * The roles the admin permissions screen hands out that the reconciler would
 * otherwise clear, and therefore the roles that need a block written for them.
 *
 * `Mod` and everything above it is left out on purpose: the reconciler already
 * preserves those on every path, by taking the higher of the account's
 * existing role and whatever its payment records grant. Writing a block for
 * them would add a fake paid subscription to an administrator's account for no
 * gain - it would make them count as a paying customer in our own reporting.
 */
export function needsOfficerGrantBlock(role: AuthRole): boolean {
  return role === AuthRole.Paid || role === AuthRole.Vip;
}

/** What setting a role by hand should do about the stored access record. */
export enum OfficerGrantAction {
  /** Write the role and a `keyData` block recording where the access came from. */
  GrantWithBlock = 'grant-with-block',
  /** Write the role and delete the hand-made grant that is no longer justified. */
  RemoveGrantBlock = 'remove-grant-block',
  /** Write the role and nothing else, which is what this screen always did. */
  RoleOnly = 'role-only',
  /** Change nothing: a real redeemed key is in the way and must not be lost. */
  RefuseRealRedemption = 'refuse-real-redemption',
  /** Change nothing: the account already has access it pays for. */
  RefuseAlreadyPaying = 'refuse-already-paying',
}

/** The one subscription of the account's own that a hand grant would displace,
 *  or `undefined` when it has none.
 *
 *  "Live" here is `isBlockLive`, NOT the reconciler's `isInGrace`. The two
 *  differ over a paused subscription, and the difference is the whole point: a
 *  paused subscriber has no access right now, so `isInGrace` says no - but the
 *  subscription is dormant rather than dead, the customer can restart it, and
 *  the app shows them a resume control to do it with. A hand grant would win
 *  the reconciler and switch that control off, so the Subscribe page would
 *  offer them a fresh purchase for a subscription they already hold. That is
 *  the double bill the pause handling exists to prevent. A checkout in flight
 *  counts for the same reason.
 *
 *  TWO blocks are deliberately left out:
 *
 *  - `keyData`, because it is the block this screen writes. Counting it would
 *    make every account this screen had ever granted refuse its own re-grant.
 *  - `coachData`, because it is not the account's own subscription. It mirrors
 *    whatever the person's COACH pays for; the account itself pays nothing and
 *    has no controls to hide. Refusing here would block an officer from
 *    comping somebody who happens to be coached, and would tell them, falsely,
 *    that the account pays for its own access. A coached person who ALSO pays
 *    is still caught, by whichever rail they pay through.
 *
 *  `coachTierData` and `coachSeatData` are kept. Those are a coach's own paid
 *  subscriptions, not a mirror of somebody else's. */
export function findGrantingSubscriptionProvider(
  info: CouchUserInfo,
  now: DateTime,
): UserLicenseType | undefined {
  const nowMillis = now.toMillis();
  const blocks: Array<{ provider: UserLicenseType; block: SubscriptionAccessWindow | undefined }> = [
    { provider: UserLicenseType.PayPro, block: info.payProData },
    { provider: UserLicenseType.Paddle, block: info.paddleData },
    { provider: UserLicenseType.GooglePlay, block: info.googlePlayData },
    { provider: UserLicenseType.Apple, block: info.appleData },
    { provider: UserLicenseType.CoachTier, block: info.coachTierData },
    { provider: UserLicenseType.CoachSeat, block: info.coachSeatData },
  ];
  // for each block the account holds, stop at the first one still live
  for (const candidate of blocks) {
    if (isBlockLive(candidate.block, nowMillis)) return candidate.provider;
  }

  // An old block can be missing the two fields checked above. The reconciler
  // fills those in from the account's top-level fields before it decides
  // anything, so a block that reads as dead here can still be alive by the
  // time it matters. Ask the top-level fields directly rather than repeating
  // that filling-in, which would be a second copy of a rule that has already
  // grown several per-provider exceptions.
  //
  // `Key` and `Coach` are excluded for the same reasons their blocks are.
  if (info.licenseType
    && info.licenseType !== UserLicenseType.Key
    && info.licenseType !== UserLicenseType.Coach
    && isBlockLive({ subscriptionStatus: info.subscriptionStatus, expiresDate_utc: info.licenseExpires_utc }, nowMillis)) {
    return info.licenseType;
  }
  return undefined;
}

/**
 * The whole decision the admin permissions screen makes, in one place.
 *
 * It lives here rather than in the page because the page cannot be unit-tested
 * in this project - there is no Angular test harness for page classes - and
 * because the tools sweep that backfills the accounts granted before this
 * existed has to reach the same answer.
 *
 * The order matters, and each step is here for its own reason:
 *
 * 1. A role that needs no block still has to clean up after one. Without this
 *    a grant could never be taken back: the role would be lowered and the
 *    never-expiring block left behind, and the next time anything recomputed
 *    access from the stored records it would put the granted role straight
 *    back. Only OUR OWN grants are deleted - a real redeemed key is somebody's
 *    purchase and is left exactly where it is, whatever role is being set.
 * 2. The refusals are checked only for the roles that would actually write a
 *    block. Setting somebody to `Mod` writes nothing, so it can destroy
 *    nothing, and refusing it because of what the account already holds would
 *    block promoting a paying customer to moderator.
 * 3. An account that is already paying is refused. The grant never expires,
 *    and the reconciler picks its winner by furthest expiry, so a
 *    never-expiring grant always beats a real dated subscription. The account
 *    would then read as key-granted, and the parts of the app that offer
 *    Cancel and Manage go by exactly that - so the customer would keep being
 *    billed with no way to stop it from inside the app. Granting access to
 *    somebody who already has it buys nothing, so there is nothing to weigh
 *    against that.
 */
export function decideOfficerGrantAction(
  chosenRole: AuthRole,
  info: CouchUserInfo,
  now: DateTime,
): OfficerGrantAction {
  if (!needsOfficerGrantBlock(chosenRole)) {
    // if the account carries a grant WE made, it is no longer justified
    if (info.keyData && !isRealKeyRedemption(info.keyData)) return OfficerGrantAction.RemoveGrantBlock;
    return OfficerGrantAction.RoleOnly;
  }
  if (isRealKeyRedemption(info.keyData)) return OfficerGrantAction.RefuseRealRedemption;
  if (findGrantingSubscriptionProvider(info, now)) return OfficerGrantAction.RefuseAlreadyPaying;
  return OfficerGrantAction.GrantWithBlock;
}

/**
 * The role an account actually gets today, as opposed to the role written on
 * it. The two differ, and the difference is the whole point of this function.
 *
 * **It asks the same question as the two places that really decide access**,
 * and neither of them is the reconciler:
 *
 *  - the server, `getUserRole` in `server/functions/src/couchdb-helpers.ts`
 *  - the browser, `getAuthRoleAndExpiry` in `client/src/services/auth.service.ts`
 *
 * All three call one shared rule, `roleAfterLicenceExpiry` in
 * `shared/src/effective-role.ts`: a role of `Paid` or below, on an account
 * whose licence end date has passed, is treated as no role at all. It never
 * applies to `Vip` or anything above it, and it cannot fire when the account
 * carries no end date, because there is nothing to compare against.
 *
 * So an account whose subscription lapsed years ago still has `paid` written
 * on it, and is nonetheless already locked out - the written role is stale
 * decoration. Anything that reasons about "who would lose access" has to ask
 * THIS question rather than reading the role, or it will overstate the answer
 * by orders of magnitude.
 *
 * **The two gates are not identical, and where they differ this takes the
 * stingier answer.** The browser has a second rule the server does not: a
 * `Waiting` subscription status - a checkout that started and never completed
 * - zeroes the role at EVERY level, `Vip` included, because the person is not
 * paying yet. Answering "they have access" for somebody one gate already
 * refuses would let a caller treat a grant as preserving something, when it
 * would really be handing out access nobody had.
 *
 * The `Waiting` rule is applied here, not in the shared rule, because the
 * server gate deliberately does not apply it (see `roleAfterLicenceExpiry`).
 * If the browser's `Waiting` rule changes, change this with it -
 * `officer-grant.test.ts` pins the cases that matter.
 */
export function resolveEffectiveRole(
  info: Pick<CouchUserInfo, 'roles' | 'licenseExpires_utc' | 'subscriptionStatus'>,
  now: DateTime,
): AuthRole {
  const writtenRole = authRoleFromStrings(info.roles);
  // if a checkout is still in flight, the browser gate gives no role at all,
  // whatever the role says and whatever the expiry is
  if (info.subscriptionStatus === SubscriptionStatus.Waiting) return AuthRole.Unknown;
  return roleAfterLicenceExpiry(writtenRole, info.licenseExpires_utc, now);
}

/**
 * Whether recording this account's access would preserve something it really
 * has today, rather than handing back access it already lost.
 *
 * This is the whole safety argument for backfilling automatically. The
 * population that needs recording is "the role is real today, but nothing
 * stored on the account justifies it", and both halves are computed:
 *
 *  - `resolveEffectiveRole` says the access is real NOW, under the same rule
 *    the app itself applies;
 *  - the caller runs the reconciler to confirm nothing stored justifies it.
 *
 * An account that already lost its access can never satisfy the first half, so
 * a backfill built on this cannot revive a lapsed subscriber. That is what
 * makes it safe to run without a person approving each row.
 *
 * **A coach's client is excluded**, and this is the one rule here that is not
 * about preserving the status quo. Their access comes from their coach's
 * subscription and is meant to follow it: `couchLogin`'s roster-gated refresh
 * rewrites their `coachData` in both directions, so a live relationship heals
 * itself and an ended one correctly stops granting. Writing a never-expiring
 * record for them would freeze access their coach has stopped paying for.
 *
 * **An account holding a live OR resumable subscription is excluded too**, for
 * the same reason the officer screen refuses that case in
 * `decideOfficerGrantAction`. A paused subscription grants no access right now,
 * so the account can look like it needs recording - but it is dormant rather
 * than dead, the customer can restart it, and the app shows a resume control to
 * do it with. A never-expiring record would win the reconciler, the account
 * would read as key-granted, and that control would switch off while the
 * subscription was still resumable at the provider. The Subscribe page would
 * then offer a fresh purchase for a subscription they already hold.
 *
 * That case is the reason this check cannot be skipped as redundant: the
 * reconciler does NOT count a paused block as granting, so such an account
 * reaches this rule looking exactly like one that needs recording. The two
 * write sites must refuse the same things, or the unattended one becomes a way
 * around the rule the interactive one enforces.
 */
export function shouldRecordExistingAccess(
  info: CouchUserInfo,
  now: DateTime,
): boolean {
  const effectiveRole = resolveEffectiveRole(info, now);
  if (!needsOfficerGrantBlock(effectiveRole)) return false;
  // if their access comes from a coach, it must keep following the coach
  if (info.licenseType === UserLicenseType.Coach) return false;
  // a real redeemed key is already a stored record; nothing to add
  if (isRealKeyRedemption(info.keyData)) return false;
  // if a subscription of their own is live or still resumable, leave it alone
  if (findGrantingSubscriptionProvider(info, now)) return false;
  return true;
}

/**
 * The record written for an account that already had access before the admin
 * screen started recording anything.
 *
 * The role is the one the account effectively holds today, and the expiry is
 * `'never'`, so the account keeps exactly the access it already had - this
 * writes down a fact rather than changing one. No officer id, because no
 * officer made this grant today; the activation code carries the provenance
 * instead.
 */
export function buildLegacyAccessRestoreKeyData(role: AuthRole, now: DateTime): CouchUserKeyData {
  return {
    subscriptionStatus: SubscriptionStatus.Active,
    expiresDate_utc: NEVER_EXPIRES,
    grantedRole: role,
    activationCode: `${LEGACY_ACCESS_RESTORE_CODE_PREFIX}-${now.toUTC().toISO()}`,
    redeemedAt_utc: now.toUTC().toISO()!,
  };
}

/**
 * The made-up activation code written on an officer's grant.
 *
 * It carries who granted it and when, so that anyone later reading the raw
 * account document can see where the access came from without needing another
 * system. The same two facts are also stored as their own fields; the code
 * repeats them because it is the field that support tooling and the older
 * scripts already look at.
 */
export function makeOfficerGrantActivationCode(officerUid: string, now: DateTime): string {
  return `${OFFICER_GRANT_CODE_PREFIX}-${officerUid}-${now.toUTC().toISO()}`;
}

/**
 * Build the `keyData` block that records an officer's hand-made grant.
 *
 * The grant never expires. That is the safe default for a comped account: an
 * expiry would silently take the access away one day, with nobody watching for
 * it. A grant meant to end is ended by an officer setting the role back, which
 * DELETES this block - see `decideOfficerGrantAction`. Lowering the role while
 * leaving the block would not end anything, because the block is what the
 * access is recomputed from.
 */
export function buildOfficerGrantKeyData(role: AuthRole, officerUid: string, now: DateTime): CouchUserKeyData {
  return {
    subscriptionStatus: SubscriptionStatus.Active,
    expiresDate_utc: NEVER_EXPIRES,
    grantedRole: role,
    activationCode: makeOfficerGrantActivationCode(officerUid, now),
    redeemedAt_utc: now.toUTC().toISO()!,
    grantedByOfficerUid: officerUid,
  };
}
